// src/pages/ReceiptTemplateSettings.jsx
import React, { useEffect, useMemo, useState } from "react";
import { FiCheck, FiSave, FiRefreshCcw, FiSearch } from "react-icons/fi";
import toast from "react-hot-toast";

import {
  useGetReceiptTemplatesQuery,
  useGetTenantReceiptTemplateQuery,
  useSaveTenantReceiptTemplateMutation,
} from "../toolkitServices/invoiceTemplateApi";

const Spinner = ({ size = 16 }) => (
  <span
    className="inline-block animate-spin rounded-full border-2 border-current border-t-transparent"
    style={{ width: size, height: size }}
  />
);

export default function ReceiptTemplateSettings() {
  const [selectedKey, setSelectedKey] = useState("");
  const [query, setQuery] = useState("");

  const {
    data: listRes,
    isLoading: listLoading,
    isFetching: listFetching,
    error: listError,
    refetch: refetchTemplates,
  } = useGetReceiptTemplatesQuery();

  const {
    data: selRes,
    isLoading: selLoading,
    isFetching: selFetching,
    error: selError,
    refetch: refetchSelected,
  } = useGetTenantReceiptTemplateQuery();

  const [saveTemplate, saveState] = useSaveTenantReceiptTemplateMutation();

  const templates = useMemo(() => {
    const arr = listRes?.templates ?? listRes?.data ?? [];
    return Array.isArray(arr) ? arr : [];
  }, [listRes]);

  const serverSelected = selRes?.receiptTemplateKey || "";

  useEffect(() => {
    if (!selectedKey && serverSelected) {
      setSelectedKey(serverSelected);
    }
  }, [serverSelected, selectedKey]);

  const filtered = useMemo(() => {
    const q = String(query || "").trim().toLowerCase();
    if (!q) return templates;

    return templates.filter((t) => {
      const name = String(t?.name || t?.templateName || "").toLowerCase();
      const key = String(t?.key || t?.templateKey || "").toLowerCase();
      return name.includes(q) || key.includes(q);
    });
  }, [templates, query]);

  const isDirty = !!selectedKey && selectedKey !== serverSelected;
  const isBusy = listLoading || selLoading || saveState.isLoading;

  const onSave = async () => {
    if (!selectedKey) {
      toast.error("Please select a receipt template");
      return;
    }

    try {
      await saveTemplate({ receiptTemplateKey: selectedKey }).unwrap();
      toast.success("Receipt template saved");
      refetchSelected();
    } catch (e) {
      toast.error(e?.data?.message || e?.error || "Failed to save");
    }
  };

  return (
    <div className="relative bg-white shadow-[0_12px_40px_-18px_rgba(15,23,42,0.35)] rounded-2xl flex flex-col h-[calc(100vh-120px)] md:h-[calc(100vh-70px)] overflow-hidden border border-slate-200">
      <div className="relative px-3 sm:px-6 py-3 text-white overflow-hidden">
        <div className="absolute inset-0 bg-[#9481ce]" />

        <div className="relative flex items-start justify-between gap-3">
          <div>
            <div className="text-[15px] sm:text-lg font-extrabold tracking-tight">
              Receipt Template
            </div>
            <div className="mt-0.5 text-[11px] sm:text-xs text-violet-100/95 leading-4">
              Choose how your customer receipts will look.
            </div>
          </div>

          <div className="flex items-center gap-2">
            {(listFetching || selFetching) && (
              <span className="hidden sm:inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-white/15">
                <Spinner size={10} /> refreshing…
              </span>
            )}

            <button
              type="button"
              onClick={() => {
                refetchTemplates();
                refetchSelected();
              }}
              className="inline-flex items-center gap-1 rounded-full bg-white/15 hover:bg-white/25 transition px-3 py-2 sm:py-1.5 text-[11px]"
              disabled={isBusy}
            >
              <FiRefreshCcw />
              <span className="hidden sm:inline">Refresh</span>
            </button>

            <button
              type="button"
              onClick={onSave}
              disabled={!isDirty || saveState.isLoading}
              className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-[#4c3fb4] text-xs font-extrabold hover:opacity-95 disabled:opacity-60 shadow-sm"
            >
              {saveState.isLoading ? <Spinner size={14} /> : <FiSave />}
              Save
            </button>
          </div>
        </div>
      </div>

      {(listError || selError) && (
        <div className="px-3 sm:px-6 py-2 text-xs border-b bg-rose-50 text-rose-700">
          {listError?.data?.message ||
            selError?.data?.message ||
            "Failed to load receipt templates"}
        </div>
      )}

      <div className="px-3 sm:px-6 py-2.5 border-b bg-slate-50/70">
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="text-[11px] sm:text-xs text-slate-700">
            <span className="text-slate-500">Current:</span>{" "}
            <span className="font-extrabold text-slate-900">
              {serverSelected || "—"}
            </span>

            {isDirty && (
              <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-[10px] font-semibold">
                unsaved
              </span>
            )}
          </div>
        </div>

        <div className="relative">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search receipt templates…"
            className="w-full pl-9 pr-3 py-2 rounded-full border border-slate-200 bg-white text-sm outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-300"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-3 sm:px-6 py-3 sm:py-4 bg-white">
        {listLoading && (
          <div className="text-sm text-slate-500 flex items-center gap-2">
            <Spinner size={16} />
            Loading receipt templates...
          </div>
        )}

        {!listLoading && filtered.length === 0 && (
          <div className="py-14 text-center">
            <div className="text-sm font-semibold text-slate-900">
              No receipt templates found
            </div>
            <div className="mt-1 text-xs text-slate-500">
              Upload receipt template first.
            </div>
          </div>
        )}

        {!listLoading && filtered.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
            {filtered.map((t, index) => {
              const key = t?.key || t?.templateKey || "";
              const name = t?.name || t?.templateName || key || "Receipt Template";

              const isSelected = selectedKey === key;
              const isCurrent = serverSelected === key;

              return (
                <button
                  key={key || index}
                  type="button"
                  onClick={() => setSelectedKey(key)}
                  className={`text-left rounded-2xl border bg-white overflow-hidden shadow-sm transition focus:outline-none ${
                    isSelected
                      ? "border-violet-400 ring-2 ring-violet-200"
                      : "border-slate-200 hover:border-violet-200"
                  }`}
                >
                  <div className="h-32 bg-slate-50 flex items-center justify-center border-b border-slate-100">
                    <div className="text-center">
                      <div className="text-lg font-extrabold text-slate-700">
                        RECEIPT
                      </div>
                      <div className="mt-1 text-[11px] text-slate-400">
                        Template Preview
                      </div>
                    </div>
                  </div>

                  <div className="p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm font-extrabold text-slate-900 truncate">
                          {name}
                        </div>
                        <div className="mt-0.5 text-[11px] text-slate-500 truncate">
                          Key: <span className="font-mono">{key}</span>
                        </div>
                        <div className="mt-0.5 text-[11px] text-slate-400 truncate">
                          File: {t?.fileName || "-"}
                        </div>
                      </div>

                      <span
                        className={`inline-flex w-2.5 h-2.5 rounded-full mt-1 ${
                          isCurrent
                            ? "bg-emerald-500"
                            : isSelected
                            ? "bg-violet-500"
                            : "bg-slate-300"
                        }`}
                      />
                    </div>

                    <div className="mt-3 flex items-center gap-2 flex-wrap">
                      <span
                        className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-semibold border ${
                          isSelected
                            ? "bg-violet-50 text-[#6b59c6] border-violet-200"
                            : "bg-white text-slate-700 border-slate-200"
                        }`}
                      >
                        {isSelected && <FiCheck />}
                        {isSelected ? "Selected" : "Tap to select"}
                      </span>

                      {isCurrent && (
                        <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-semibold border bg-emerald-50 text-emerald-700 border-emerald-200">
                          Active
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="sm:hidden border-t bg-white px-3 py-3">
        <button
          type="button"
          onClick={onSave}
          disabled={!isDirty || saveState.isLoading}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[#6b59c6] text-white text-sm font-extrabold hover:opacity-90 disabled:opacity-50 shadow-sm"
        >
          {saveState.isLoading ? <Spinner size={16} /> : <FiSave />}
          Save Selection
        </button>
        <div className="mt-2 text-[10px] text-slate-500 text-center">
          This will apply for all new receipts.
        </div>
      </div>

      {saveState.isLoading && (
        <div className="absolute inset-0 z-40 flex items-center justify-center bg-white/65 backdrop-blur-[1px]">
          <div className="flex flex-col items-center gap-2 text-slate-700 text-xs sm:text-sm bg-white border border-slate-200 rounded-2xl px-5 py-4 shadow-lg">
            <Spinner size={22} />
            <span className="font-semibold">Saving receipt template…</span>
          </div>
        </div>
      )}
    </div>
  );
}